import { T, Var } from 'gt-react';
import { PlusIcon } from '@phosphor-icons/react';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Label } from 'src/components/ui/label';
import type { ExceptionTypeOption } from './ExceptionRuleEditorRow';
import { exceptionTypeCatalog, type RuleDraft } from './exceptionRuleSetDraft';

/**
 * Every option of the selected exception vocabulary. Types without a rule
 * still open on invoices (unassigned); this list is how you see them all
 * without opening a per-rule type picker.
 */
export function ExceptionTypeCatalog({
  options,
  rules,
  disabled,
  onAddRule,
  onAddRemaining,
}: {
  options: ExceptionTypeOption[];
  rules: readonly RuleDraft[];
  disabled: boolean;
  onAddRule: (optionId: string) => void;
  onAddRemaining: () => void;
}) {
  const rows = exceptionTypeCatalog(
    options.map((option) => option.id),
    rules,
  );
  const remainingCount = rows.filter((row) => row.ruleNumber === null).length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-medium">
          <T context="exception rules: catalog heading">All Exception Types</T>
        </Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || remainingCount === 0}
          onClick={onAddRemaining}
        >
          <PlusIcon className="mr-1 h-4 w-4" />
          <T context="exception rules: add a rule for every type that does not have one yet">
            Add Remaining Types
          </T>
        </Button>
      </div>
      {options.length === 0 ? (
        <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          <T context="exception rules: catalog empty because the enum has no options">
            This enum has no options yet. Edit options to add exception types.
          </T>
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {options.map((option, index) => (
            <ExceptionTypeCatalogRow
              key={option.id}
              option={option}
              ruleNumber={rows[index]?.ruleNumber ?? null}
              disabled={disabled}
              onAddRule={() => onAddRule(option.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ExceptionTypeCatalogRow({
  option,
  ruleNumber,
  disabled,
  onAddRule,
}: {
  option: ExceptionTypeOption;
  ruleNumber: number | null;
  disabled: boolean;
  onAddRule: () => void;
}) {
  return (
    <li className="flex items-start gap-2 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{option.name}</div>
        {option.description ? (
          <p className="break-words text-xs text-muted-foreground">
            {option.description}
          </p>
        ) : null}
      </div>
      {ruleNumber !== null ? (
        <Badge variant="outline" className="shrink-0 text-[10px]">
          <T context="exception rules: catalog badge showing which rule already uses this type">
            Rule <Var>{ruleNumber}</Var>
          </T>
        </Badge>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="shrink-0"
          disabled={disabled}
          onClick={onAddRule}
        >
          <PlusIcon className="mr-1 h-4 w-4" />
          <T context="exception rules: add a rule for this catalog type">
            Add Rule
          </T>
        </Button>
      )}
    </li>
  );
}
